import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import * as mcquizApi from '../services/mcquizApi';
import { useMcQuizStore } from '../stores/mcquizStore';

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function McQuizTestBuilder() {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = testId && testId !== 'new';

  const { currentTest, sections, setCurrentTest, addSection, removeSection, updateSection, reset } =
    useMcQuizStore();

  // Form state
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [testType, setTestType] = useState('mock_test');
  const [timerMinutes, setTimerMinutes] = useState<number | ''>('');
  const [totalMarks, setTotalMarks] = useState<number | ''>('');
  const [marksPerQuestion, setMarksPerQuestion] = useState<number | ''>(2);
  const [negativeRatio, setNegativeRatio] = useState<number | ''>(0.33);
  const [accessLevel, setAccessLevel] = useState('free');
  const [category, setCategory] = useState('');

  // Section creation
  const [newSectionTitle, setNewSectionTitle] = useState('');

  // Question search
  const [activeSectionId, setActiveSectionId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSubject, setSearchSubject] = useState('');
  const [searchDifficulty, setSearchDifficulty] = useState('');
  const [searchBankId, setSearchBankId] = useState('');
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);

  // Auto-populate state
  const [autoPopSubject, setAutoPopSubject] = useState('');
  const [autoPopDifficulty, setAutoPopDifficulty] = useState('');
  const [autoPopCount, setAutoPopCount] = useState(10);

  // Load existing test
  const { isLoading: loadingTest } = useQuery({
    queryKey: ['mcquiz-test', testId],
    queryFn: () => mcquizApi.getTest(Number(testId)).then((r) => r.data),
    enabled: !!isEdit,
    onSuccess: (data: any) => {
      const test = data.test ?? data;
      setCurrentTest(test);
      setTitle(test.title || '');
      setSlug(test.slug || '');
      setDescription(test.description || '');
      setTestType(test.test_type || 'mock_test');
      setTimerMinutes(test.timer_minutes ?? '');
      setTotalMarks(test.total_marks ?? '');
      setMarksPerQuestion(test.marks_per_question ?? 2);
      setNegativeRatio(test.negative_ratio ?? 0.33);
      setAccessLevel(test.access_level || 'free');
      setCategory(test.category || '');
    },
  } as any);

  // Load banks for search filter
  const { data: banksData } = useQuery({
    queryKey: ['mcquiz-banks'],
    queryFn: () => mcquizApi.listBanks().then((r) => r.data),
  });
  const banks: any[] = banksData?.items ?? banksData?.banks ?? (Array.isArray(banksData) ? banksData : []);

  // Question search
  const { data: searchResults, isFetching: searching } = useQuery({
    queryKey: ['mcquiz-search', searchQuery, searchSubject, searchDifficulty, searchBankId],
    queryFn: () =>
      mcquizApi
        .searchQuestions({
          q: searchQuery || undefined,
          subject: searchSubject || undefined,
          difficulty: searchDifficulty || undefined,
          bank_id: searchBankId ? Number(searchBankId) : undefined,
          limit: 50,
        })
        .then((r) => r.data),
    enabled: activeSectionId !== null,
  });
  const questions: any[] =
    searchResults?.items ?? searchResults?.questions ?? (Array.isArray(searchResults) ? searchResults : []);

  // Auto-slug
  useEffect(() => {
    if (!isEdit) {
      setSlug(slugify(title));
    }
  }, [title, isEdit]);

  // Cleanup on unmount
  useEffect(() => () => reset(), [reset]);

  // Save / Create
  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        title,
        slug,
        description: description || undefined,
        test_type: testType,
        timer_minutes: timerMinutes || undefined,
        total_marks: totalMarks || undefined,
        marks_per_question: marksPerQuestion || undefined,
        negative_ratio: negativeRatio || undefined,
        access_level: accessLevel,
        category: category || undefined,
      };
      if (isEdit) {
        return mcquizApi.updateTest(Number(testId), payload);
      }
      return mcquizApi.createTest(payload as mcquizApi.CreateTestData);
    },
    onSuccess: (res) => {
      toast.success(isEdit ? 'Test updated' : 'Test created');
      if (!isEdit) {
        const newId = res.data?.id ?? res.data?.test_id ?? res.data?.test?.id;
        if (newId) navigate(`/mcquiz/tests/${newId}`, { replace: true });
      }
      queryClient.invalidateQueries({ queryKey: ['mcquiz-tests'] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.detail || 'Save failed'),
  });

  // Add section
  const addSectionMutation = useMutation({
    mutationFn: () => {
      if (!isEdit) throw new Error('Save the test first');
      return mcquizApi.addSection(Number(testId), {
        title: newSectionTitle.trim() || `Section ${sections.length + 1}`,
        position: sections.length,
      });
    },
    onSuccess: (res) => {
      const section = res.data?.section ?? res.data;
      addSection(section);
      setNewSectionTitle('');
      toast.success('Section added');
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.detail || 'Failed to add section'),
  });

  // Delete section
  const deleteSectionMutation = useMutation({
    mutationFn: (sectionId: number) => mcquizApi.deleteSection(Number(testId), sectionId),
    onSuccess: (_, sectionId) => {
      removeSection(sectionId);
      toast.success('Section removed');
    },
    onError: () => toast.error('Failed to remove section'),
  });

  // Assign questions
  const assignMutation = useMutation({
    mutationFn: () => {
      if (!activeSectionId || selectedQuestionIds.length === 0) throw new Error('Select questions');
      return mcquizApi.assignQuestions(Number(testId), activeSectionId, {
        question_ids: selectedQuestionIds,
        marks_per_question: Number(marksPerQuestion) || 2,
      });
    },
    onSuccess: () => {
      toast.success(`${selectedQuestionIds.length} question(s) assigned`);
      setSelectedQuestionIds([]);
      queryClient.invalidateQueries({ queryKey: ['mcquiz-test', testId] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.detail || 'Failed to assign questions'),
  });

  // Auto-populate
  const autoPopMutation = useMutation({
    mutationFn: () => {
      return mcquizApi.autoPopulate(Number(testId), {
        subjects: autoPopSubject ? [autoPopSubject] : undefined,
        difficulty: autoPopDifficulty || undefined,
        count: autoPopCount,
      });
    },
    onSuccess: () => {
      toast.success('Questions auto-populated');
      queryClient.invalidateQueries({ queryKey: ['mcquiz-test', testId] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.detail || 'Auto-populate failed'),
  });

  // Lifecycle
  const previewMutation = useMutation({
    mutationFn: () => mcquizApi.moveToPreview(Number(testId)),
    onSuccess: () => {
      toast.success('Moved to preview');
      navigate(`/mcquiz/tests/${testId}/preview`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Failed'),
  });

  const publishMutation = useMutation({
    mutationFn: () => mcquizApi.publishTest(Number(testId)),
    onSuccess: () => {
      toast.success('Test published');
      navigate('/mcquiz/tests');
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Failed'),
  });

  const toggleQuestionSelection = (qId: number) => {
    setSelectedQuestionIds((prev) =>
      prev.includes(qId) ? prev.filter((id) => id !== qId) : [...prev, qId],
    );
  };

  if (loadingTest && isEdit) {
    return (
      <div className="flex items-center justify-center py-24">
        <ArrowPathIcon className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
        {isEdit ? 'Edit Test' : 'Create Test'}
      </h1>

      {/* ------------------------------------------------------------------ */}
      {/* Test meta form                                                      */}
      {/* ------------------------------------------------------------------ */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Test Details</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Slug
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm font-mono"
            />
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm resize-none"
            />
          </div>

          {/* Test type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Test Type
            </label>
            <select
              value={testType}
              onChange={(e) => setTestType(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
            >
              <option value="mock_test">Mock Test</option>
              <option value="sectional">Sectional</option>
              <option value="practice_set">Practice Set</option>
            </select>
          </div>

          {/* Timer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Timer (minutes)
            </label>
            <input
              type="number"
              min={0}
              value={timerMinutes}
              onChange={(e) => setTimerMinutes(e.target.value ? Number(e.target.value) : '')}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
            />
          </div>

          {/* Total marks */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Total Marks
            </label>
            <input
              type="number"
              min={0}
              value={totalMarks}
              onChange={(e) => setTotalMarks(e.target.value ? Number(e.target.value) : '')}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
            />
          </div>

          {/* Marks per question */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Marks per Question
            </label>
            <input
              type="number"
              min={0}
              step={0.5}
              value={marksPerQuestion}
              onChange={(e) => setMarksPerQuestion(e.target.value ? Number(e.target.value) : '')}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
            />
          </div>

          {/* Negative marking ratio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Negative Marking Ratio
            </label>
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={negativeRatio}
              onChange={(e) => setNegativeRatio(e.target.value ? Number(e.target.value) : '')}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
            />
          </div>

          {/* Access level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Access Level
            </label>
            <select
              value={accessLevel}
              onChange={(e) => setAccessLevel(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
            >
              <option value="free">Free</option>
              <option value="premium">Premium</option>
            </select>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. UPSC Prelims"
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
            />
          </div>
        </div>

        {/* Save button */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!title.trim() || saveMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          >
            {saveMutation.isPending ? 'Saving...' : isEdit ? 'Update Test' : 'Create Test'}
          </button>
          {isEdit && (
            <>
              <button
                onClick={() => previewMutation.mutate()}
                disabled={previewMutation.isPending}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 transition-colors text-sm"
              >
                Preview
              </button>
              <button
                onClick={() => publishMutation.mutate()}
                disabled={publishMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-sm"
              >
                Publish
              </button>
            </>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Sections                                                            */}
      {/* ------------------------------------------------------------------ */}
      {isEdit && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Sections</h2>

          {sections.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No sections yet. Add one to start assigning questions.
            </p>
          ) : (
            <div className="space-y-3">
              {sections.map((section, idx) => (
                <div
                  key={section.id}
                  className={`border rounded-lg p-4 transition-colors ${
                    activeSectionId === section.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() =>
                        setActiveSectionId(
                          activeSectionId === section.id ? null : section.id,
                        )
                      }
                      className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      {activeSectionId === section.id ? (
                        <ChevronUpIcon className="w-4 h-4" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4" />
                      )}
                      {section.title}
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ({section.question_count ?? section.questions?.length ?? 0} questions)
                      </span>
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete section "${section.title}"?`)) {
                          deleteSectionMutation.mutate(section.id);
                        }
                      }}
                      className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Question assignment panel */}
                  {activeSectionId === section.id && (
                    <div className="mt-4 space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                      {/* Auto-populate */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                          <SparklesIcon className="w-4 h-4" />
                          Auto-populate
                        </div>
                        <div className="flex flex-wrap items-end gap-2">
                          <input
                            type="text"
                            placeholder="Subject"
                            value={autoPopSubject}
                            onChange={(e) => setAutoPopSubject(e.target.value)}
                            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1 text-xs w-28"
                          />
                          <select
                            value={autoPopDifficulty}
                            onChange={(e) => setAutoPopDifficulty(e.target.value)}
                            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1 text-xs"
                          >
                            <option value="">Any difficulty</option>
                            <option value="Easy">Easy</option>
                            <option value="Medium">Medium</option>
                            <option value="Hard">Hard</option>
                          </select>
                          <input
                            type="number"
                            min={1}
                            max={200}
                            value={autoPopCount}
                            onChange={(e) => setAutoPopCount(Number(e.target.value))}
                            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1 text-xs w-16"
                          />
                          <button
                            onClick={() => autoPopMutation.mutate()}
                            disabled={autoPopMutation.isPending}
                            className="px-3 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 disabled:opacity-50"
                          >
                            {autoPopMutation.isPending ? 'Working...' : 'Fill'}
                          </button>
                        </div>
                      </div>

                      {/* Manual search */}
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Search &amp; assign questions
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <div className="relative flex-1 min-w-[180px]">
                            <MagnifyingGlassIcon className="w-4 h-4 absolute left-2.5 top-2 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search text..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="w-full pl-8 pr-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                            />
                          </div>
                          <input
                            type="text"
                            placeholder="Subject"
                            value={searchSubject}
                            onChange={(e) => setSearchSubject(e.target.value)}
                            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1.5 text-xs w-28"
                          />
                          <select
                            value={searchDifficulty}
                            onChange={(e) => setSearchDifficulty(e.target.value)}
                            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1.5 text-xs"
                          >
                            <option value="">Any difficulty</option>
                            <option value="Easy">Easy</option>
                            <option value="Medium">Medium</option>
                            <option value="Hard">Hard</option>
                          </select>
                          <select
                            value={searchBankId}
                            onChange={(e) => setSearchBankId(e.target.value)}
                            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1.5 text-xs"
                          >
                            <option value="">All banks</option>
                            {banks.map((b: any) => (
                              <option key={b.id} value={b.id}>
                                {b.title}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Results */}
                        <div className="max-h-64 overflow-y-auto border rounded border-gray-200 dark:border-gray-700">
                          {searching ? (
                            <div className="flex justify-center py-6">
                              <ArrowPathIcon className="w-4 h-4 animate-spin text-gray-400" />
                            </div>
                          ) : questions.length === 0 ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400 p-4 text-center">
                              No questions found
                            </p>
                          ) : (
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                              <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                                <tr>
                                  <th className="px-2 py-1.5 text-left w-8">
                                    <input
                                      type="checkbox"
                                      checked={
                                        questions.length > 0 &&
                                        questions.every((q: any) =>
                                          selectedQuestionIds.includes(q.id),
                                        )
                                      }
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedQuestionIds(questions.map((q: any) => q.id));
                                        } else {
                                          setSelectedQuestionIds([]);
                                        }
                                      }}
                                      className="rounded"
                                    />
                                  </th>
                                  <th className="px-2 py-1.5 text-left text-gray-500 dark:text-gray-400">
                                    ID
                                  </th>
                                  <th className="px-2 py-1.5 text-left text-gray-500 dark:text-gray-400">
                                    Question
                                  </th>
                                  <th className="px-2 py-1.5 text-left text-gray-500 dark:text-gray-400">
                                    Subject
                                  </th>
                                  <th className="px-2 py-1.5 text-left text-gray-500 dark:text-gray-400">
                                    Difficulty
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {questions.map((q: any) => (
                                  <tr
                                    key={q.id}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer"
                                    onClick={() => toggleQuestionSelection(q.id)}
                                  >
                                    <td className="px-2 py-1.5">
                                      <input
                                        type="checkbox"
                                        checked={selectedQuestionIds.includes(q.id)}
                                        onChange={() => toggleQuestionSelection(q.id)}
                                        className="rounded"
                                      />
                                    </td>
                                    <td className="px-2 py-1.5 text-gray-600 dark:text-gray-300">
                                      {q.id}
                                    </td>
                                    <td className="px-2 py-1.5 text-gray-900 dark:text-white max-w-[300px] truncate">
                                      {q.question_text || q.question || '-'}
                                    </td>
                                    <td className="px-2 py-1.5 text-gray-600 dark:text-gray-300">
                                      {q.subject || '-'}
                                    </td>
                                    <td className="px-2 py-1.5 text-gray-600 dark:text-gray-300 capitalize">
                                      {q.difficulty || '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>

                        {selectedQuestionIds.length > 0 && (
                          <button
                            onClick={() => assignMutation.mutate()}
                            disabled={assignMutation.isPending}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
                          >
                            {assignMutation.isPending
                              ? 'Assigning...'
                              : `Assign ${selectedQuestionIds.length} question(s)`}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add section */}
          <div className="flex items-center gap-2 pt-2">
            <input
              type="text"
              placeholder="New section title"
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
              className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') addSectionMutation.mutate();
              }}
            />
            <button
              onClick={() => addSectionMutation.mutate()}
              disabled={addSectionMutation.isPending}
              className="inline-flex items-center gap-1 px-4 py-2 bg-gray-900 dark:bg-gray-600 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-500 disabled:opacity-50 transition-colors text-sm"
            >
              <PlusIcon className="w-4 h-4" />
              Add Section
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
